import styled from 'styled-components';
import React, { FC, useEffect, useRef } from 'react';
import { ArrowUpward as ArrowUpwardIcon } from '@material-ui/icons';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { push as Push } from 'redux-first-history';
import { useDispatch, useSelector } from 'react-redux';
import { AutomationContactType, CompressionFormat, ExportFileFormats } from '@dev/ee-api/ee-api-models';
import { Container, Paper, PrimaryButton, SecondaryButton, TabsPanel } from '@dev/ee-components';
import { generateIdBasedOnPath, safelyDecodeBase64Url } from '@dev/ee-tools';

import ExportModal from '@/components/ExportModal';
import { AutomationPaths, pathsParams } from '@/services/dictionaries/Paths';
import { GetAutomationContactsStart, getAutomationStore, ListFromAutomation } from '@/store/automations/actions';
import { ExportAutomationContacts } from '@/store/campaigns/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { ListsLoadStart } from '@/store/lists/actions';
import { ExportAutomationParams, ListFromAutomationParams } from '@/services/ElasticApi';

import ContactsFilters from './components/ContactsFilters';
import ContactsPagination from './components/ContactsPagination';
import ContactsTable from './components/ContactsTable';
import { AddContactsToList } from '../../../Contacts/CommonActions/AddToList';

const FieldsFooter = styled.div`
    text-align: right;
    button + button {
        margin-left: 16px;
    }
`;

const getScreenName = (recipentsContactsType: AutomationContactType): string => {
    switch (recipentsContactsType) {
        case AutomationContactType.Completed:
            return 'recipientshavecompletedthisautomation';
        case AutomationContactType.InProgress:
            return 'recipientsarecurrentlyinthisautomation';
        case AutomationContactType.All:
        default:
            return 'totalrecipients';
    }
};

const ViewContacts: FC = (): JSX.Element => {
    const { contactsListPagination } = useSelector(getAutomationStore);
    const urlParams: { name: string; recipientsType: AutomationContactType } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const params = { name: safelyDecodeBase64Url(urlParams.name), type: urlParams.recipientsType };

    const route = useRouteMatch<{ type: string }>();
    useEffect(() => {
        dispatch(GetAutomationContactsStart(params));
        dispatch(ListsLoadStart({ getAllElements: true }));
    }, []);

    const selectedList = useRef({ list: '', newList: false });
    const exportCompressionFormat = useRef(CompressionFormat.Zip);
    const exportFileFormat = useRef(ExportFileFormats.Csv);

    let fileNameActivityRef: HTMLInputElement;

    const routeForIds =
        route?.path?.replace?.(pathsParams.name, '')?.replace(pathsParams.type, route.params?.type) ?? '';

    return (
        <Container style={{ paddingTop: '74px', paddingLeft: '9px', paddingRight: '9px' }}>
            <TabsPanel
                breadcrumbs={safelyDecodeBase64Url(urlParams.name)}
                title={t(getScreenName(urlParams.recipientsType))}
                breadcrumbsOnClick={(): void => {
                    dispatch(Push(AutomationPaths.AutomationReport.replace(':name', urlParams.name)));
                }}
                additionalHeaderContent={
                    <FieldsFooter>
                        <SecondaryButton
                            id={generateIdBasedOnPath('create-list-button', routeForIds)}
                            text={t('createlist')}
                            onClick={(): void => {
                                dispatch(
                                    ShowDialog({
                                        title: t('addcontactsctolist'),
                                        body: (
                                            <AddContactsToList
                                                moveContacts={false}
                                                onChange={(ev, selectedItems) => {
                                                    selectedList.current = { list: selectedItems.trim(), newList: ev };
                                                }}
                                            />
                                        ),
                                        confirmLabel: t('save'),
                                        cancelLabel: t('cancel'),
                                        onClickConfirm: (): void => {
                                            const addContactsToListRequestParams: ListFromAutomationParams = {
                                                journeyName: params.name,
                                                listName: selectedList.current.list,
                                                from: contactsListPagination.from,
                                                to: contactsListPagination.to,
                                                newList: selectedList.current.newList,
                                                type: params.type,
                                            };

                                            dispatch(ListFromAutomation(addContactsToListRequestParams));
                                        },
                                        isHiddenManually: true,
                                    })
                                );
                            }}
                        />
                        <PrimaryButton
                            text={t('export')}
                            onClick={(): void => {
                                dispatch(
                                    ShowDialog({
                                        title: t('export'),
                                        body: (
                                            <ExportModal
                                                onCompressionChange={(enabled: boolean): void => {
                                                    exportCompressionFormat.current = enabled
                                                        ? CompressionFormat.Zip
                                                        : CompressionFormat.None;
                                                }}
                                                onFileFormatChange={(data: {
                                                    value: ExportFileFormats;
                                                    label: string;
                                                }): void => {
                                                    exportFileFormat.current = data.value;
                                                }}
                                                inputRef={(element) => {
                                                    fileNameActivityRef = element;
                                                }}
                                            />
                                        ),
                                        confirmLabel: t('export'),
                                        cancelLabel: t('cancel'),
                                        onClickConfirm: (): void => {
                                            const exportEventsRequestParams: ExportAutomationParams = {
                                                name: params.name,
                                                type: params.type,
                                                from: contactsListPagination.from,
                                                to: contactsListPagination.to,
                                                fileFormat: exportFileFormat.current,
                                                compressionFormat: exportCompressionFormat.current,
                                                fileName: fileNameActivityRef?.value || 'export',
                                            };
                                            dispatch(ExportAutomationContacts(exportEventsRequestParams));
                                        },
                                    })
                                );
                            }}
                            startIcon={<ArrowUpwardIcon />}
                        />
                    </FieldsFooter>
                }
            />
            <Paper>
                <ContactsFilters />
                <ContactsTable />
                <ContactsPagination />
            </Paper>
        </Container>
    );
};

export default ViewContacts;
